import { BaseDataService } from './BaseDataService';
import { Organization, Team, SecondaryReport, UserCharacteristicToggle } from '../../models';
import { NotificationService } from '../../services';
import { OpenBlobFile } from '../../utilities';

export class ReportDataService extends BaseDataService<any> {
    constructor(notificationService: NotificationService) {
        super('Report', 'Report', notificationService);
    }

    public async createEmailRequestForLimitedIndividualReport(surveyInstanceId: number, reportTypeId: number, includeTypes: boolean) : Promise<any> {
        return await this.createEmailRequestForIndividualReport(
            surveyInstanceId, reportTypeId, 'IndividualShort', includeTypes
        );
    }

    public async createEmailRequestForFullIndividualReport(surveyInstanceId: number, reportTypeId: number, includeTypes: boolean) : Promise<any> {
        return await this.createEmailRequestForIndividualReport(
            surveyInstanceId, reportTypeId, 'IndividualLong', includeTypes
        );
    }
    
    public getIndividualReportDataLatest(userId: number, surveyVersionId?: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/individualReport/latest/${userId}`;
        if (surveyVersionId) {
            endpoint = `${this.endpointRoot}/individualReport/version/${userId}/${surveyVersionId}`;
        }

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving individual report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getIndividualReportDataByInstance(id: number, surveyVersionId?: number): Promise<any> {
        var endpoint = `${this.endpointRoot}/individualReport/instance/${id}`;
        if (surveyVersionId) {
            endpoint = `${endpoint}/${surveyVersionId}`;
        }

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving individual report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    private getIndividualPDF(endpoint: string, id: number, downloadName: string, reportId?: number): Promise<any> {
        if (reportId) {
            endpoint = `${endpoint}/${reportId}`;
        }
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            // this returns a SurveyAssessmentViewModel
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving survey responses.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            return OpenBlobFile(blobData, downloadName + '.pdf');
        });
    }
    public getIndividualBriefPDF(id: number, downloadName: string, reportId?: number): Promise<any> {
        return this.getIndividualPDF(
            `${this.endpointRoot}/individualBrief/pdf/${id}`,
            id, downloadName, reportId
        );
    }
    public getTeamologyPDF(surveyVersionId: number, teamId: number): Promise<any> {
        const endpoint = `${this.endpointRoot}/teamologyReport/pdf/${surveyVersionId}/${teamId}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving teamology data.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            return OpenBlobFile(blobData, 'Teamology Report.pdf');
        });
    }
    public getIndividualReportDataPDF(id: number, downloadName: string, reportId?: number): Promise<any> {
        return this.getIndividualPDF(
            `${this.endpointRoot}/individualReport/pdf/${id}`,
            id, downloadName, reportId
        );
    }
    public getNewUserGuidePDF(downloadName: string) {
        var endpoint = `${this.endpointRoot}/LimitedReport/newUserGuide/`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving survey responses.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            return OpenBlobFile(blobData, downloadName + '.pdf');
        });
    }
    public getLimitedStylePDF(id: number, downloadName: string): Promise<any> {
        var endpoint = `${this.endpointRoot}/LimitedReport/style/${id}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            // this returns a SurveyAssessmentViewModel
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving survey responses.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            return OpenBlobFile(blobData, downloadName + '.pdf');
        });
    }
    public getStylePDF(id: number, downloadName: string): Promise<any> {
        var endpoint = `${this.endpointRoot}/style/pdf/${id}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            // this returns a SurveyAssessmentViewModel
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving survey responses.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            return OpenBlobFile(blobData, downloadName + '.pdf');
        });
    }
    
    public getResponsesPDFByTeam(questionId: number, teamId: number, anonymous: boolean): Promise<any> {
        var endpoint = `${this.endpointRoot}/responses/pdf/byTeam/${questionId}/${teamId}/${anonymous}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            // this returns a SurveyAssessmentViewModel
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving survey responses.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            return OpenBlobFile(blobData, 'Responses_' + questionId + '.pdf');
        });
    }

    public getResponsesPDFByOrganization(questionId: number, orgId: number, anonymous: boolean): Promise<any> {
        var endpoint = `${this.endpointRoot}/responses/pdf/byOrg/${questionId}/${orgId}/${anonymous}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            // this returns a SurveyAssessmentViewModel
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving survey responses.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            return OpenBlobFile(blobData, 'Responses_' + questionId + '.pdf');
        });
    }

    public getResponsesPDFByCandidates(questionId: number, orgId: number, anonymous: boolean): Promise<any> {
        var endpoint = `${this.endpointRoot}/responses/pdf/byCandidates/${questionId}/${orgId}/${anonymous}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            // this returns a SurveyAssessmentViewModel
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving survey responses.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            return OpenBlobFile(blobData, 'Responses_' + questionId + '.pdf');
        });
    }

    public getResponsesPDFByFormer(questionId: number, orgId: number, anonymous: boolean): Promise<any> {
        var endpoint = `${this.endpointRoot}/responses/pdf/byFormer/${questionId}/${orgId}/${anonymous}`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            // this returns a SurveyAssessmentViewModel
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving responses pdf.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            return OpenBlobFile(blobData, 'Responses_' + questionId + '.pdf');
        });
    }

    public getManagerReportDataPDF(surveyVersionId: number, userId: number, downloadName: string, reportId?: number): Promise<any> {

        var endpoint = `${this.endpointRoot}/managerReport/pdf/${surveyVersionId}/${userId}`;
        if (reportId) {
            endpoint += `/${reportId}`;
        }
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            // this returns a SurveyAssessmentViewModel
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving manager report pdf.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            return OpenBlobFile(blobData, downloadName + '.pdf');
        });
    }   

    public getComparisonReportDataPDF(id: number, userIds: number[], markedCharacteristics: UserCharacteristicToggle[], downloadName: string, reportId?: number): Promise<any> {

        var endpoint = `${this.endpointRoot}/comparisonReport/pdf/${id}`;
        if (reportId) {
            endpoint += `/${reportId}`;
        }
        const init = this.buildRequestInit('POST', {
            userIds: userIds,
            markedCharacteristics: markedCharacteristics
        });

        return fetch(endpoint, init).then((response) => {
            // this returns a SurveyAssessmentViewModel
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving comparison report pdf.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            return OpenBlobFile(blobData, downloadName + '.pdf');
        });
    }

    public getIndividualReportDataLatestByReport(userId: number, secondaryReport: SecondaryReport, surveyVersionId?: number): Promise<any> {
        var endpoint = `${this.endpointRoot}/individualReport/latest/${userId}/${secondaryReport.id}`;
        if (surveyVersionId) {
            endpoint = `${endpoint}/${surveyVersionId}`;
        }

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving individual report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getIndividualReportDataLatestBySurveyVersion(userId: number, surveyVersionId: number): Promise<any> {
        var endpoint = `${this.endpointRoot}/individualReport/latestByVersion/${userId}/${surveyVersionId}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving individual report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }
    
    public getIndividualReportData(surveyId?: number, userId?: number, surveyVersionId?: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/individualReport/${surveyId}`;

        if (userId) {
            endpoint = `${endpoint}/${userId}`;
            if (surveyVersionId) {
                endpoint = `${endpoint}/${surveyVersionId}`;
            }
        }

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            // const message = `Error retrieving individual report`;
            // this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getTeamGrid(teamId: number, surveyVersionId: number, page: number, pageSize: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/teamGrid/${teamId}/${surveyVersionId}?page=${page}&pageSize=${pageSize}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            // const message = `Error retrieving individual report`;
            // this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getTeamGridByOrg(orgId: number, surveyVersionId: number, page: number, pageSize: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/teamGrid/byOrg/${orgId}/${surveyVersionId}?page=${page}&pageSize=${pageSize}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            // const message = `Error retrieving individual report`;
            // this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getTeamGridByOrgCandidates(orgId: number, surveyVersionId: number, page: number, pageSize: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/teamGrid/byOrg/candidates/${orgId}/${surveyVersionId}?page=${page}&pageSize=${pageSize}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            // const message = `Error retrieving individual report`;
            // this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getTeamGridByOrgFormer(orgId: number, surveyVersionId: number, page: number, pageSize: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/teamGrid/byOrg/former/${orgId}/${surveyVersionId}?page=${page}&pageSize=${pageSize}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            // const message = `Error retrieving individual report`;
            // this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getIndividualReportStructureData(surveyVersionId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/individualReport/structure/${surveyVersionId}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving individual report structure`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getIndividualReportDataByReport(secondaryReport: SecondaryReport, userId?: number, surveyVersionId?: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/individualReportByReport/${secondaryReport.id}`;

        if (userId) {
            endpoint = `${endpoint}/${userId}`;
            if (surveyVersionId) {
                endpoint = `${endpoint}/${surveyVersionId}`;
            }
        }

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving individual report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }
    public getIndividualReportStructureDataByReport(secondaryReport: SecondaryReport): Promise<any> {
        let endpoint = `${this.endpointRoot}/individualReport/structureByReport/${secondaryReport.id}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving individual report structure`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }
    
    public getIndividualReportDataByVersion(surveyId: number, surveyVersionId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/individualReport/byVersion/${surveyId}/${surveyVersionId}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving individual report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }
    
    public getResponseIndicatorsData(surveyId?: number, userId?: number, surveyVersionId?: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/responseIndicators/${surveyId}`;

        if (userId) {
            endpoint = `${endpoint}/${userId}`;
            if (surveyVersionId) {
                endpoint = `${endpoint}/${surveyVersionId}`;
            }
        }

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving response indicators`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }
    
    public getManagersReportData(surveyVersionId: number, userId?: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/managersReport/${surveyVersionId}`;

        if (userId) {
            endpoint = `${endpoint}/${userId}`;
        }

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving managers report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getTeamologyOrganizationReportData(organization: Organization, surveyId: number, surveyVersionId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/teamologyReport/organization/${surveyId}/${organization.id}/${surveyVersionId}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error('Network response was not ok.');
        });
    }

    public getTeamologyCandidateReportData(organization: Organization, surveyId: number, surveyVersionId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/teamologyReport/candidates/${surveyId}/${organization.id}/${surveyVersionId}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getTeamologyFormerEmployeeReportData(organization: Organization, surveyId: number, surveyVersionId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/teamologyReport/former/${surveyId}/${organization.id}/${surveyVersionId}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getTeamologyTeamReportData(team: Team, surveyId: number, surveyVersionId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/teamologyReport/team/${surveyId}/${team.id}/${surveyVersionId}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getTeamReportData(team: Team, surveyVersionId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/teamReport/team/${surveyVersionId}/${team.id}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getTeamOrganizationReportData(organization: Organization, surveyVersionId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/teamReport/organization/${surveyVersionId}/${organization.id}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getTeamCandidateReportData(organization: Organization, surveyVersionId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/teamReport/candidates/${surveyVersionId}/${organization.id}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getTeamFormerEmployeeReportData(organization: Organization, surveyVersionId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/teamReport/former/${surveyVersionId}/${organization.id}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getTeamListViewReportByOrganization(surveyVersionId: number, orgId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/teamListView/organization/${surveyVersionId}/org/${orgId}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getTeamListViewReportByFormer(surveyVersionId: number, orgId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/teamListView/former/${surveyVersionId}/org/${orgId}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getTeamListViewReportByCandidates(surveyVersionId: number, orgId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/teamListView/candidates/${surveyVersionId}/org/${orgId}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getTeamListViewReportByTeam(surveyVersionId: number, teamId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/teamListView/${surveyVersionId}/team/${teamId}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getReadynessReportByOrganization(surveyVersionId: number, orgId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/readyness/byOrg/${orgId}/${surveyVersionId}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getReadynessReportByFormer(surveyVersionId: number, orgId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/readyness/byOrg/former/${orgId}/${surveyVersionId}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getReadynessReportByCandidates(surveyVersionId: number, orgId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/readyness/byOrg/candidates/${orgId}/${surveyVersionId}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    public getReadynessReportByTeam(surveyVersionId: number, teamId: number): Promise<any> {
        let endpoint = `${this.endpointRoot}/readyness/${teamId}/${surveyVersionId}`;

        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }
            const message = `Error retrieving report`;
            this.notificationService.addError(message);
            throw new Error('Network response was not ok.');
        });
    }

    protected async createEmailRequestForIndividualReport(surveyInstanceId: number,
                                                          reportTypeId: number,
                                                          reportType: string,
                                                          includeTypes: boolean) : Promise<any> {
        let endpoint = `${this.endpointRoot}/EmailRequests/${reportType}`;
        const init = this.buildRequestInit('POST', {surveyInstanceId, reportTypeId, includeTypes});
        return await fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return {success: true, response: response.json()};
            }
            this.notificationService.addError(`Failed to create email request for individual report. Survey instance ID: '${surveyInstanceId}'.`);
            return {success: false, response: response.json()};
        });
    }
}


// WEBPACK FOOTER //
// ./src/services/api/ReportData.tsx